import { useState, useEffect, useMemo, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import {
  Dumbbell,
  User,
  CheckCircle2,
  XCircle,
  Clock,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  X,
  Search,
  Calendar as CalendarIcon,
  Check,
  ChevronsUpDown,
} from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { key: "sunday", label: "Sun" },
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
];

export function MemberWorkoutTab({ member }) {
  const { hasPermission } = useAuth();

  // ── Permission flags ──────────────────────────────────────
  const canAdd = hasPermission("add_workout");
  const canDelete = hasPermission("delete_workout");

  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState(null);
  const [trainer, setTrainer] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [stats, setStats] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    setSelectedDay(null);
    fetchWorkoutData();
  }, [member.id]);

  const fetchWorkoutData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/workouts/assignments/member/${member.id}`);
      setAssignment(res.data.assignment);
      setTrainer(res.data.trainer);
      const fetchedSchedule = res.data.schedule || [];
      setSchedule(fetchedSchedule);
      setStats(res.data.stats);

      if (fetchedSchedule.length > 0) {
        const today = new Date();
        const todayStr = format(today, "yyyy-MM-dd");
        setSelectedDay((prev) => {
          if (prev) {
            const prevStr = format(prev.date, "yyyy-MM-dd");
            const updatedEntry = fetchedSchedule.find((s) => s.date === prevStr);
            return {
              date: prev.date,
              schedule: updatedEntry || { status: "no workouts", workouts: [] }
            };
          }
          const todayEntry = fetchedSchedule.find((s) => s.date === todayStr);
          return {
            date: today,
            schedule: todayEntry || { status: "no workouts", workouts: [] }
          };
        });
      }
    } catch (err) {
      console.error("Failed to fetch workout data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async (scheduleId) => {
    if (!window.confirm("Are you sure you want to mark this workout as completed?")) return;
    try {
      await api.put(`/workouts/schedule/${scheduleId}/status`, { status: "completed" });
      toast.success("Workout marked as completed");
      fetchWorkoutData();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleMarkUncomplete = async (scheduleId) => {
    if (!window.confirm("Are you sure you want to mark this workout as uncompleted?")) return;
    try {
      await api.put(`/workouts/schedule/${scheduleId}/status`, { status: "upcoming" });
      toast.success("Workout marked as uncompleted");
      fetchWorkoutData();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteAssignment = async () => {
    if (!assignment) return;
    try {
      await api.delete(`/workouts/assignments/${assignment.id}`);
      toast.success("Assignment deleted");
      setSelectedDay(null);
      fetchWorkoutData();
    } catch (err) {
      toast.error("Failed to delete assignment");
    }
  };

  const getScheduleForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return schedule.find((s) => s.date === dateStr);
  };

  const handleDayClick = (day) => {
    const scheduleEntry = getScheduleForDate(day);
    if (scheduleEntry) setSelectedDay({ date: day, schedule: scheduleEntry });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 md:gap-4" data-testid="member-workout-tab">

      {/* ── Header ── */}
      <div className="relative z-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-2">
        <div className="flex items-center gap-4">
          {trainer ? (
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-primary/10 text-primary">
                  {trainer.name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Assigned Trainer</p>
                <p className="font-medium">{trainer.name}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <User className="h-5 w-5" />
              <span className="text-sm">No trainer assigned</span>
            </div>
          )}
        </div>

        {/* Action buttons — gated */}
        <div className="flex flex-wrap gap-2">
          {/* Assign / Reassign — needs add_workout */}
          {canAdd && (
            <Button
              onClick={() => setShowAssignModal(true)}
              className="font-heading uppercase tracking-wide text-xs"
              data-testid="assign-workouts-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              {assignment ? "Reassign Workouts" : "Assign Workouts"}
            </Button>
          )}
          {/* Delete Assignment — needs delete_workout */}
          {canDelete && assignment && (
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              className="text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
              data-testid="delete-assignment-btn"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* ── Stats ── */}
      {stats && (
        <div className="relative z-0 grid grid-cols-3 sm:grid-cols-3 gap-2 md:gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-2 md:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Completed</p>
                  <p className="text-2xl font-black font-heading text-emerald-500">{stats.completed}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-emerald-500/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-2 md:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Remaining</p>
                  <p className="text-2xl font-black font-heading text-amber-500">{stats.remaining}</p>
                </div>
                <Clock className="h-8 w-8 text-amber-500/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-2 md:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Missed</p>
                  <p className="text-2xl font-black font-heading text-red-500">{stats.missed}</p>
                </div>
                <XCircle className="h-5 w-5 sm:h-8 sm:w-8 text-red-500/30" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Calendar + Side Panel ── */}
      {assignment && schedule.length > 0 ? (
        <div className="relative z-0 grid grid-cols-1 lg:grid-cols-12 gap-2">
          {/* Calendar */}
          <Card className="bg-card border-border lg:col-span-5">
            <CardHeader className="flex flex-row items-center justify-between px-3 py-2">
              <CardTitle className="text-sm font-heading uppercase tracking-wide">Calendar</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium min-w-[120px] text-center">
                  {format(currentMonth, "MMMM yyyy")}
                </span>
                <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-1">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {DAYS_OF_WEEK.map((day) => (
                  <div key={day.key} className="text-center text-[10px] font-mono uppercase tracking-widest text-muted-foreground py-2">
                    {day.label}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-0.5">
                {eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) }).map((day, idx) => {
                  const dayOfWeek = day.getDay();
                  const isFirstDay = idx === 0;
                  const scheduleEntry = getScheduleForDate(day);
                  const status = scheduleEntry?.status;
                  const isSelected =
                    selectedDay &&
                    format(selectedDay.date, "yyyy-MM-dd") === format(day, "yyyy-MM-dd");
                  return (
                    <div
                      key={day.toISOString()}
                      style={{ gridColumn: isFirstDay ? dayOfWeek + 1 : undefined }}
                      onClick={() => handleDayClick(day)}
                      className={`min-h-[60px] p-1.5 rounded-md border cursor-pointer transition-all ${isSelected
                        ? "border-primary ring-2 ring-primary/20"
                        : status === "completed"
                          ? "border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-500/50"
                          : status === "missed"
                            ? "border-red-500/30 bg-red-500/5 hover:border-red-500/50"
                            : status
                              ? "border-amber-500/30 bg-amber-500/5 hover:border-amber-500/50"
                              : "border-border bg-secondary/30 hover:bg-secondary/50"
                        }`}
                    >
                      <div className="text-[10px] font-medium mb-1">{format(day, "d")}</div>
                      {scheduleEntry && (
                        <div className="space-y-1">
                          <div className="flex flex-wrap gap-0.5">
                            {scheduleEntry.workouts?.slice(0, 1).map((w, i) => (
                              <TooltipProvider key={i} delayDuration={30}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-[8px] text-muted-foreground truncate block w-full">{w.title}</span>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-popover text-popover-foreground border border-border p-2.5 max-w-[280px]" onClick={(e) => e.stopPropagation()}>
                                    {scheduleEntry.workouts?.slice(1).map((w, i) => (
                                      <p key={i} className="text-[10px] text-muted-foreground font-mono">{w.title}</p>
                                    ))}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ))}
                            {scheduleEntry.workouts?.length > 1 && (
                              <span className="text-[8px] text-primary flex gap-1">
                                <span>+{scheduleEntry.workouts.length - 1}</span><span className="hidden md:block">more</span>
                              </span>
                            )}
                          </div>
                          <div className={`h-1.5 w-1.5 rounded-full ${status === "completed" ? "bg-emerald-500"
                            : status === "missed" ? "bg-red-500"
                              : "bg-amber-500"
                            }`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-xs text-muted-foreground">Completed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-xs text-muted-foreground">Missed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <span className="text-xs text-muted-foreground">Upcoming</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Side Panel */}
          <Card className="bg-card border-border lg:col-span-7">
            <CardHeader className=" px-3 py-2 border-b border-border">
              <CardTitle className="text-sm font-heading uppercase tracking-wide flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" />
                {selectedDay ? format(selectedDay.date, "EEEE, MMM d") : "Select a Day"}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 py-2">
              {selectedDay ? (
                <div className="space-y-2">
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <Badge className={`border text-xs font-mono uppercase ${selectedDay.schedule.status === "completed"
                        ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/20"
                        : selectedDay.schedule.status === "missed"
                          ? "bg-red-500/15 text-red-500 border-red-500/20"
                          : "bg-amber-500/15 text-amber-500 border-amber-500/20"
                        }`}>
                        {selectedDay.schedule.status}
                      </Badge>
                      {selectedDay.schedule.status === "upcoming" && (
                        <Button
                          size="sm"
                          className="text-xs h-7"
                          onClick={() => handleMarkComplete(selectedDay.schedule.id)}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Mark Complete
                        </Button>
                      )}
                      {selectedDay.schedule.status === "completed" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 border-amber-500/30 text-amber-500 hover:bg-amber-500/10 hover:text-amber-500"
                          onClick={() => handleMarkUncomplete(selectedDay.schedule.id)}
                        >
                          <XCircle className="h-3.5 w-3.5 mr-1" />Mark Uncomplete
                        </Button>
                      )}
                    </div>
                    {selectedDay.schedule.status === "completed" && selectedDay.schedule.completedAt && (() => {
                      const date = new Date(selectedDay.schedule.completedAt);
                      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                      const day = date.getDate();
                      const month = months[date.getMonth()];
                      const year = date.getFullYear();
                      let hours = date.getHours();
                      const minutes = String(date.getMinutes()).padStart(2, "0");
                      const ampm = hours >= 12 ? "pm" : "am";
                      hours = hours % 12;
                      hours = hours ? hours : 12;
                      const displayDateTime = `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
                      const isMember = selectedDay.schedule.completedBy?.startsWith("member:");

                      return (
                        <div className="flex text-xs text-muted-foreground font-mono mt-1.5 space-y-0.5">
                          <p className="font-semibold text-foreground">
                            Completed by {isMember ? "Member" : "Staff"}: {selectedDay.schedule.completedByName || (isMember ? "Member" : "Staff")}
                          </p>
                          <p className="text-muted-foreground/60 font-semibold leading-none">|</p>
                          <p className="text-muted-foreground font-medium">{displayDateTime}</p>
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                      Assigned Workouts ({selectedDay.schedule.workouts?.length || 0})
                    </p>
                    {selectedDay.schedule.workouts?.length > 0 ? (
                      <Accordion type="multiple" className="space-y-2">
                        {selectedDay.schedule.workouts.map((workout, idx) => (
                          <AccordionItem
                            key={workout.id || idx}
                            value={workout.id || `workout-${idx}`}
                            className="border border-border rounded-lg overflow-hidden"
                          >
                            <AccordionTrigger className="px-3 py-2 hover:no-underline hover:bg-secondary/50">
                              <div className="flex items-center gap-3 text-left">
                                <div className="h-8 w-8 rounded-md bg-secondary flex items-center justify-center shrink-0 overflow-hidden">
                                  {workout.image
                                    ? <img src={`${BACKEND_URL}${workout.image}`} alt={workout.title} className="h-full w-full object-cover" />
                                    : <Dumbbell className="h-4 w-4 text-muted-foreground" />}
                                </div>
                                <span className="font-medium text-sm">{workout.title}</span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-3 pb-3">
                              <div className="space-y-3 pt-2 border-t border-border">
                                {workout.image && (
                                  <img
                                    src={`${BACKEND_URL}${workout.image}`}
                                    alt={workout.title}
                                    className="w-full h-32 object-cover rounded-md"
                                  />
                                )}
                                <div>
                                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Description</p>
                                  <p className="text-sm text-muted-foreground">
                                    <span dangerouslySetInnerHTML={{ __html: workout.description }} />
                                  </p>
                                </div>
                                {(workout.groupNames?.length > 0 || workout.tagNames?.length > 0) && (
                                  <div className="flex flex-wrap gap-1">
                                    {workout.groupNames?.map((name, i) => (
                                      <Badge key={`g-${i}`} variant="outline" className="text-[10px]">{name}</Badge>
                                    ))}
                                    {workout.tagNames?.map((name, i) => (
                                      <Badge key={`t-${i}`} variant="secondary" className="text-[10px]">{name}</Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    ) : (
                      <p className="text-sm text-muted-foreground">No workouts assigned</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <CalendarIcon className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Click on a highlighted day to view workout details
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="relative z-0">
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <Dumbbell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-heading font-semibold mb-2">No Workouts Assigned</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Assign workouts to this member to track their progress
              </p>
              {/* Empty state assign btn — gated */}
              {canAdd && (
                <Button onClick={() => setShowAssignModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />Assign Workouts
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Assign Modal ── */}
      <AssignWorkoutModal
        open={showAssignModal}
        onOpenChange={setShowAssignModal}
        member={member}
        existingAssignment={assignment}
        existingTrainer={trainer}
        onSuccess={fetchWorkoutData}
      />

      {/* ── Delete Confirmation ── */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-wide">
              Delete Workout Assignment?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove all assigned workouts and the entire schedule for{" "}
              <span className="font-semibold text-foreground">{member.name}</span>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteAssignment}
            >
              <Trash2 className="h-4 w-4 mr-2" />Delete Assignment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── WorkoutSelect ──────────────────────────────────────────────────────────────────────────────────
function WorkoutSelect({ workouts, selectedIds, onSelect, onRemove }) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(0);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  const handleUnselect = (workoutId) => {
    onRemove(workoutId);
  };

  const handleClearAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    selectedIds.forEach((id) => onRemove(id));
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex min-h-[42px] h-auto w-full items-center justify-between rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background transition-all hover:border-primary/50 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary cursor-text",
            open && "ring-2 ring-primary/20 border-primary"
          )}
          onClick={() => inputRef.current?.focus()}
        >
          <div className="flex flex-wrap gap-1.5 items-center flex-1 min-w-0">
            {selectedIds.length > 0 &&
              selectedIds.map((workoutId) => {
                const workout = workouts.find((w) => w.id === workoutId);
                return (
                  <Badge
                    key={workoutId}
                    variant="secondary"
                    className="rounded-full px-2.5 py-0.5 text-xs font-medium bg-primary/20 text-primary border-primary/30 gap-1 hover:bg-primary/30 transition-colors animate-in fade-in zoom-in-95 duration-150"
                  >
                    {workout?.title || workoutId}
                    <button
                      type="button"
                      className="rounded-full outline-none focus:ring-1 focus:ring-primary ml-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleUnselect(workoutId);
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleUnselect(workoutId);
                      }}
                    >
                      <X className="h-3 w-3 text-primary/70 hover:text-primary transition-colors" />
                    </button>
                  </Badge>
                );
              })
            }
            <input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onFocus={() => setOpen(true)}
              placeholder={selectedIds.length === 0 ? "Select workouts..." : ""}
              className="ml-1 flex-1 bg-transparent outline-none placeholder:text-muted-foreground text-foreground min-w-[80px]"
            />
          </div>
          <div className="flex items-center gap-2 ml-auto shrink-0 pl-2">
            {selectedIds.length > 0 && (
              <X
                className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                onClick={handleClearAll}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground opacity-50" />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-popover shadow-xl border border-border rounded-xl overflow-hidden"
        align="start"
        style={{ width: triggerWidth > 0 ? triggerWidth : "auto", minWidth: "240px" }}
      >
        <Command
          className="border-none w-full"
          filter={(value, search) => {
            const workout = workouts.find((w) => String(w.id) === value);
            if (!workout) return 0;
            const q = search.toLowerCase();
            if (
              workout.title?.toLowerCase().includes(q) ||
              workout.shortDescription?.toLowerCase().includes(q)
            ) {
              return 1;
            }
            return 0;
          }}
        >
          <div className="flex items-center border-b px-3 bg-muted/30">
            <CommandInput
              placeholder="Search workouts..."
              className="border-none focus:ring-0"
              value={inputValue}
              onValueChange={setInputValue}
            />
          </div>
          <CommandList className="max-h-[280px]">
            <CommandEmpty className="py-6 text-center text-muted-foreground text-sm">
              No workouts found.
            </CommandEmpty>
            <CommandGroup className="p-1 mb-1">
              {workouts.map((workout) => {
                const isSelected = selectedIds.includes(workout.id);
                return (
                  <CommandItem
                    key={workout.id}
                    value={String(workout.id)}
                    onSelect={() => {
                      if (isSelected) {
                        onRemove(workout.id);
                      } else {
                        onSelect(workout.id);
                      }
                    }}
                    className={cn(
                      "flex items-center justify-between px-3 py-2.5 cursor-pointer transition-colors mb-1 rounded-md",
                      isSelected ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                    )}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={cn(
                        "h-8 w-8 rounded-md flex items-center justify-center shrink-0 overflow-hidden",
                        isSelected ? "bg-primary-foreground/10" : "bg-secondary"
                      )}>
                        {workout.image ? (
                          <img
                            src={`${BACKEND_URL}${workout.image}`}
                            alt={workout.title}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <Dumbbell className={cn("h-4 w-4", isSelected ? "text-primary-foreground" : "text-muted-foreground")} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={cn("text-sm font-medium truncate leading-tight", isSelected ? "text-primary-foreground" : "text-foreground")}>
                          {workout.title}
                        </p>
                        {workout.shortDescription && (
                          <p className={cn("text-[11px] truncate mt-0.5", isSelected ? "text-primary-foreground/75" : "text-muted-foreground")}>
                            {workout.shortDescription}
                          </p>
                        )}
                      </div>
                    </div>
                    {isSelected ? (
                      <Check className="h-4 w-4 text-primary-foreground shrink-0 ml-2" />
                    ) : (
                      <Plus className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/20">
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">
                {selectedIds.length} selected
              </span>
              {selectedIds.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="text-xs text-destructive hover:text-destructive/80 font-semibold transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-primary hover:text-primary/80 font-bold transition-colors uppercase tracking-wider"
            >
              Done
            </button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── AssignWorkoutModal ─────────────────────────────────────────────────────────────────────────────────────
function AssignWorkoutModal({ open, onOpenChange, member, existingAssignment, existingTrainer, onSuccess }) {
  const [trainers, setTrainers] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedTrainer, setSelectedTrainer] = useState("");
  const [numberOfDays, setNumberOfDays] = useState(30);
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [autoOverlap, setAutoOverlap] = useState(false);
  const [allowMemberToComplete, setAllowMemberToComplete] = useState(false);

  const defaultSchedule = {
    sunday: { enabled: false, useGroup: false, groupId: "", workoutIds: [] },
    monday: { enabled: true, useGroup: false, groupId: "", workoutIds: [] },
    tuesday: { enabled: true, useGroup: false, groupId: "", workoutIds: [] },
    wednesday: { enabled: true, useGroup: false, groupId: "", workoutIds: [] },
    thursday: { enabled: true, useGroup: false, groupId: "", workoutIds: [] },
    friday: { enabled: true, useGroup: false, groupId: "", workoutIds: [] },
    saturday: { enabled: false, useGroup: false, groupId: "", workoutIds: [] },
  };

  const [weeklySchedule, setWeeklySchedule] = useState(defaultSchedule);

  useEffect(() => {
    if (open) {
      fetchData();
      if (existingAssignment) {
        setSelectedTrainer(existingAssignment.trainerId || "");
        setNumberOfDays(existingAssignment.numberOfDays || 30);
        setStartDate(existingAssignment.startDate || format(new Date(), "yyyy-MM-dd"));
        setAutoOverlap(existingAssignment.autoOverlap || false);
        setAllowMemberToComplete(existingAssignment.allowMemberToComplete || false);
        if (existingAssignment.weeklySchedule) {
          const newSchedule = { ...defaultSchedule };
          DAYS_OF_WEEK.forEach(({ key }) => {
            const dayWorkouts = existingAssignment.weeklySchedule[key] || [];
            newSchedule[key] = { enabled: dayWorkouts.length > 0, useGroup: false, groupId: "", workoutIds: dayWorkouts };
          });
          setWeeklySchedule(newSchedule);
        }
      } else {
        setSelectedTrainer("");
        setNumberOfDays(30);
        setStartDate(format(new Date(), "yyyy-MM-dd"));
        setAutoOverlap(false);
        setAllowMemberToComplete(false);
        setWeeklySchedule(defaultSchedule);
      }
    }
  }, [open, existingAssignment]);

  const fetchData = async () => {
    try {
      const [trainersRes, workoutsRes, groupsRes] = await Promise.all([
        api.get("/workouts/trainers/list"),
        api.get("/workouts"),
        api.get("/workouts/groups"),
      ]);
      setTrainers(trainersRes.data.trainers || []);
      setWorkouts(workoutsRes.data.workouts || []);
      setGroups(groupsRes.data.groups || []);
    } catch (err) {
      toast.error("Failed to load data");
    }
  };

  const handleDayToggle = (day) =>
    setWeeklySchedule((prev) => ({ ...prev, [day]: { ...prev[day], enabled: !prev[day].enabled } }));

  const handleUseGroupToggle = (day) =>
    setWeeklySchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], useGroup: !prev[day].useGroup, groupId: "", workoutIds: [] },
    }));

  const handleGroupSelect = (day, groupId) => {
    const groupWorkouts = workouts.filter((w) => w.groupIds?.includes(groupId)).map((w) => w.id);
    setWeeklySchedule((prev) => ({ ...prev, [day]: { ...prev[day], groupId, workoutIds: groupWorkouts } }));
  };

  const handleWorkoutAdd = (day, workoutId) =>
    setWeeklySchedule((prev) => {
      const current = prev[day].workoutIds || [];
      if (!current.includes(workoutId)) return { ...prev, [day]: { ...prev[day], workoutIds: [...current, workoutId] } };
      return prev;
    });

  const handleWorkoutRemove = (day, workoutId) =>
    setWeeklySchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], workoutIds: (prev[day].workoutIds || []).filter((id) => id !== workoutId) },
    }));

  const handleSubmit = async () => {
    const schedule = {};
    DAYS_OF_WEEK.forEach(({ key }) => {
      const dayConfig = weeklySchedule[key];
      if (dayConfig.enabled && dayConfig.workoutIds.length > 0) schedule[key] = dayConfig.workoutIds;
    });
    if (Object.keys(schedule).length === 0) {
      toast.error("Please select at least one workout for any day");
      return;
    }
    setLoading(true);
    try {
      await api.post("/workouts/assignments", {
        memberId: member.id,
        trainerId: selectedTrainer && selectedTrainer !== "none" ? selectedTrainer : null,
        weeklySchedule: schedule,
        numberOfDays: parseInt(numberOfDays),
        startDate,
        autoOverlap,
        allowMemberToComplete,
      });
      toast.success("Workouts assigned successfully");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign workouts");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 py-5 border-b border-border shrink-0">
          <DialogTitle className="font-heading uppercase tracking-wide">
            {existingAssignment ? "Reassign Workouts" : "Assign Workouts"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-2 space-y-2 md:px-6 md:py-6  ">
          <div className="flex items-center gap-4 p-4 bg-secondary/30 rounded-xl">
            <Avatar className="h-12 w-12">
              {member.photo ? <AvatarImage src={`${BACKEND_URL}${member.photo}`} /> : null}
              <AvatarFallback className="bg-primary text-primary-foreground">
                {member.name?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-heading font-semibold">{member.name}</p>
              <p className="text-sm text-muted-foreground">{member.email}</p>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-xs font-heading uppercase">Weekly Schedule</Label>
            <div className="flex flex-col gap-3">
              {DAYS_OF_WEEK.map(({ key, label }) => (
                <div
                  key={key}
                  className={`px-3 py-2 rounded-xl border ${weeklySchedule[key].enabled ? "border-primary/30 bg-primary/5" : "border-border bg-secondary/20"
                    }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={weeklySchedule[key].enabled}
                        onCheckedChange={() => handleDayToggle(key)}
                        id={`day-${key}`}
                      />
                      <label htmlFor={`day-${key}`} className="font-heading font-semibold cursor-pointer">
                        {label}day
                      </label>
                      {weeklySchedule[key].enabled && weeklySchedule[key].workoutIds.length > 0 && (
                        <Badge className="bg-primary/15 text-primary border-primary/20 border text-[10px] font-mono">
                          {weeklySchedule[key].workoutIds.length}
                        </Badge>
                      )}
                    </div>
                    {weeklySchedule[key].enabled && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Assign by Group</span>
                        <Switch
                          checked={weeklySchedule[key].useGroup}
                          onCheckedChange={() => handleUseGroupToggle(key)}
                        />
                      </div>
                    )}
                  </div>
                  {weeklySchedule[key].enabled && (
                    <div className="space-y-3">
                      {weeklySchedule[key].useGroup && (
                        <Select
                          value={weeklySchedule[key].groupId || "select"}
                          onValueChange={(v) => v !== "select" && handleGroupSelect(key, v)}
                        >
                          <SelectTrigger><SelectValue placeholder="Select workout group" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="select" disabled>Select workout group</SelectItem>
                            {groups.map((g) => (
                              <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <WorkoutSelect
                        workouts={workouts}
                        selectedIds={weeklySchedule[key].workoutIds || []}
                        onSelect={(workoutId) => handleWorkoutAdd(key, workoutId)}
                        onRemove={(workoutId) => handleWorkoutRemove(key, workoutId)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">Number of Workout Days</Label>
              <Input type="number" value={numberOfDays} onChange={(e) => setNumberOfDays(e.target.value)} min={1} max={365} />
              <p className="text-xs text-muted-foreground">Total workout days to complete</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-heading uppercase">Assign Trainer</Label>
            <Select value={selectedTrainer || "none"} onValueChange={setSelectedTrainer}>
              <SelectTrigger><SelectValue placeholder="Select trainer (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Trainer</SelectItem>
                {trainers.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name} ({t.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl">
            <div>
              <p className="font-medium text-sm">Auto Calculate Overlap Days</p>
              <p className="text-xs text-muted-foreground">If member misses a workout, system pushes schedule forward</p>
            </div>
            <Switch checked={autoOverlap} onCheckedChange={setAutoOverlap} />
          </div>

          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl">
            <div>
              <p className="font-medium text-sm">Allow Member to Complete Workouts</p>
              <p className="text-xs text-muted-foreground">Allow the member to mark workouts as completed in their portal</p>
            </div>
            <Switch checked={allowMemberToComplete} onCheckedChange={setAllowMemberToComplete} />
          </div>
        </div>


        <DialogFooter className="px-6 py-4 border-t border-border shrink-0 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "Assigning..." : (existingAssignment ? "Reassign" : "Assign Workouts")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
